import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCompany } from '@/context/CompanyContext';

export interface Withdrawal {
  id: string;
  company_id: string;
  user_id: string;
  product_id: string;
  product_name: string;
  quantity_withdrawn: number;
  withdrawal_date: string;
  reason?: string | null;
  seller_name?: string | null;
}

export function useWithdrawals() {
  const { currentCompany } = useCompany();
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchWithdrawals = useCallback(async () => {
    if (!currentCompany) return;
    setLoading(true);
    try {
      // First get withdrawals
      const { data: withdrawalsData, error: withdrawalsError } = await supabase
        .from('withdrawals')
        .select('*')
        .eq('company_id', currentCompany.id)
        .order('withdrawal_date', { ascending: false });

      if (withdrawalsError) throw withdrawalsError;

      // Then get user profiles for the seller names
      if (!withdrawalsData?.length) {
        setWithdrawals([]);
        return;
      }

      // Build profiles map (best-effort). If RPC fails, continue without seller names
      const userIds = [...new Set(withdrawalsData.map((w: any) => w.user_id))];

      let profilesMap: Record<string, string> = {};
      try {
        if (userIds.length > 0) {
          const { data: profilesData, error: profilesError } = await supabase
            .rpc('get_withdrawal_user_profiles', { 
              p_company_id: currentCompany.id, 
              p_user_ids: userIds 
            });

          if (!profilesError && profilesData) {
            profilesMap = (profilesData as any[]).reduce((acc: Record<string, string>, profile: any) => {
              acc[profile.user_id] = profile.full_name;
              return acc;
            }, {});
          } else if (profilesError) {
            console.warn('[useWithdrawals] Profiles RPC failed, continuing without seller names', profilesError);
          }
        }
      } catch (err) {
        console.warn('[useWithdrawals] Profiles lookup errored, continuing without seller names', err);
      }

      const mapped: Withdrawal[] = withdrawalsData.map((w: any) => ({
        id: w.id,
        company_id: w.company_id,
        user_id: w.user_id,
        product_id: w.product_id,
        product_name: w.product_name,
        quantity_withdrawn: w.quantity_withdrawn,
        withdrawal_date: w.withdrawal_date,
        reason: w.reason,
        seller_name: profilesMap[w.user_id] ?? null,
      }));

      setWithdrawals(mapped);
    } catch (e) {
      console.error('Failed to fetch withdrawals', e);
    } finally {
      setLoading(false);
    }
  }, [currentCompany]);

  const processWithdrawal = useCallback(async (productId: string, quantity: number, reason?: string) => {
    if (!currentCompany) return false;
    try {
      const { data, error } = await supabase.rpc('process_withdrawal', {
        p_company_id: currentCompany.id,
        p_product_id: productId,
        p_quantity: quantity,
        p_reason: reason ?? 'Retrait stock',
      });
      if (error) throw error;
      await fetchWithdrawals();
      return !!data;
    } catch (e) {
      console.error('Failed to process withdrawal', e);
      return false;
    }
  }, [currentCompany, fetchWithdrawals]);

  useEffect(() => {
    fetchWithdrawals();
  }, [fetchWithdrawals]);

  return { withdrawals, loading, processWithdrawal, refetch: fetchWithdrawals };
}
